'use client'

import { useState, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { sharedStatusNotice } from '@/components/santa-chat/chat-notices'
import { SantaChatScreen, type SantaChatCopy } from '@/components/santa-chat/santa-chat-screen'
import type { PublicChatAppLabels, SignupPromptReason } from '@/lib/free-santa'
import type { PublicLocale } from '@/lib/locale-prefix'
import type { ChatState } from '@/lib/santa-chat/chat-state'
import { ClosingCard } from './closing-card'
import { ConsentPanel } from './consent-panel'
import type { PublicSession } from './public-session'
import { SignupPrompt } from './signup-prompt'
import { usePublicSantaChat, type PublicChatPhase } from './use-public-santa-chat'

interface PublicSantaChatAppProps {
  locale: PublicLocale
  labels: PublicChatAppLabels
  /** La copy de la pantalla (la del chat del niño), ya resuelta en servidor para este idioma. */
  copy: SantaChatCopy
  /** La barra del sitio (marca y vuelta a la home), pintada en servidor. */
  siteBar?: ReactNode
}

function messagesLeftText(labels: PublicChatAppLabels, count: number): string {
  const template = count === 1 ? labels.messagesLeftOne : labels.messagesLeftOther
  return template.replace('{count}', String(count))
}

interface PublicStatusProps {
  state: ChatState
  copy: SantaChatCopy
  labels: PublicChatAppLabels
  locale: PublicLocale
  session: PublicSession
}

/** Sobre el compositor, como mucho un aviso: el cierre, los comunes con el chat del niño o los mensajes que quedan. */
function PublicStatus({ state, copy, labels, locale, session }: PublicStatusProps) {
  if (session.closing) return <ClosingCard labels={labels.closing} offer={session.closing} />
  const shared = sharedStatusNotice({ state, copy, locale })
  if (shared) return shared
  if (!session.quota) return null
  return (
    <p role="status" className="readable-body mb-2 text-center text-sm text-muted-foreground tabular-nums">
      {messagesLeftText(labels, session.quota.left)}
    </p>
  )
}

interface GateProps {
  phase: Exclude<PublicChatPhase, 'chat'>
  labels: PublicChatAppLabels
  chat: ReturnType<typeof usePublicSantaChat>
}

/**
 * Antes del chat, al lado del mismo panel: el consentimiento o, si este
 * dispositivo ya habló hoy, el cierre. Hasta saberlo (antes de hidratar), nada:
 * así quien vuelve no ve parpadear el formulario.
 */
function Gate({ phase, labels, chat }: GateProps): ReactNode {
  if (phase === 'unknown') return <div aria-hidden className="min-h-48" />
  if (phase === 'usedToday') {
    return (
      <div className="readable-body mx-auto flex w-full max-w-md flex-col gap-4 p-6 text-center">
        <h1 className="font-christmas text-3xl font-bold">{labels.usedToday.title}</h1>
        <p className="text-muted-foreground">{labels.usedToday.body}</p>
        <ClosingCard labels={labels.closing} offer="full" />
      </div>
    )
  }
  const open = chat.session.open
  return (
    <ConsentPanel
      labels={labels.consent}
      busy={open.status === 'busy'}
      error={open.status === 'failed' ? open.error : null}
      onStart={(name, token) => void chat.open(name, token)}
    />
  )
}

/**
 * El chat público con Papá Noel como pantalla de app: la misma del chat del
 * niño (panel con sus gestos, notas de voz con ▶ y temporizador, sonidos), con
 * dos cambios: el micrófono y «llamar» invitan a crear la cuenta, y el tope de
 * la sesión cierra con el alta. Se entra solo tras el consentimiento; el panel
 * de Papá Noel es el mismo antes y después, sin volver a montarse.
 */
export function PublicSantaChatApp({ locale, labels, copy, siteBar }: PublicSantaChatAppProps) {
  const router = useRouter()
  const chat = usePublicSantaChat(locale, labels)
  // Montado desde la primera vez que se pide y nunca desmontado: al cerrarse, conserva su animación de salida.
  const [prompt, setPrompt] = useState<{ open: boolean; reason: SignupPromptReason } | null>(null)
  const ask = (reason: SignupPromptReason) => setPrompt({ open: true, reason })

  return (
    <SantaChatScreen
      state={chat.state}
      copy={copy}
      voice={chat.voice}
      header={{ back: () => router.push(labels.backHref) }}
      onCall={() => ask('call')}
      onSendText={chat.send}
      voiceInput={{ kind: 'locked', onLocked: () => ask('voice') }}
      onResend={chat.resend}
      siteBar={siteBar}
      gate={chat.phase === 'chat' ? undefined : <Gate phase={chat.phase} labels={labels} chat={chat} />}
      statusNotice={<PublicStatus state={chat.state} copy={copy} labels={labels} locale={locale} session={chat.session} />}
      overlay={
        prompt && (
          <SignupPrompt
            open={prompt.open}
            reason={prompt.reason}
            labels={labels.signupPrompt}
            signupHref={labels.closing.signup.href}
            onClose={() => setPrompt({ ...prompt, open: false })}
          />
        )
      }
    />
  )
}
